// CouriersListXL - Sprint 44 Fase 3 Vista 6.
// Card con header (icono+titulo+buscador) + lista de filas con logo,
// nombre + badges (inactivo/con rastreo), pais, acciones Editar/Desactivar.
// XLSkeleton en loading. Tokens + dark.
import { useEffect } from 'react';
import { ensureKeyframes, injectCSS } from '../../design/motion';
import XLBadge from '../../components/xl/XLBadge';
import XLSkeleton from '../../components/xl/XLSkeleton';
import { IconTruck } from './components/Icons';

const CSS_ID = 'xl-cou-list-css';
const CSS = `
.xl-cou-card {
  background: var(--xl-color-surface);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg);
  box-shadow: var(--xl-shadow-sm);
  overflow: hidden;
  font-family: var(--xl-font-sans);
}
.xl-cou-card__hd {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--xl-color-border);
}
.xl-cou-card__title {
  font-size: 13px; font-weight: 600; color: var(--xl-color-text);
  display: inline-flex; align-items: center; gap: 8px; margin: 0;
}
.xl-cou-card__title-ico { color: var(--xl-color-info) }

.xl-cou-search {
  font: 400 13px/1.4 var(--xl-font-sans);
  color: var(--xl-color-text);
  background: var(--xl-color-bg);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-md);
  padding: 7px 10px;
  min-width: 180px;
  box-sizing: border-box;
}
.xl-cou-search:focus {
  outline: none;
  border-color: var(--xl-color-focus);
  box-shadow: 0 0 0 3px rgba(37,99,235,0.18);
}
[data-xl-theme="dark"] .xl-cou-search:focus {
  box-shadow: 0 0 0 3px rgba(96,165,250,0.28);
}

.xl-cou-row {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--xl-color-border);
  transition: background 120ms cubic-bezier(0,0,0.2,1);
}
.xl-cou-row:last-child { border-bottom: 0 }
.xl-cou-row:hover { background: var(--xl-color-surface-alt) }
.xl-cou-row__left { display: flex; align-items: center; gap: 12px; min-width: 0 }
.xl-cou-row__avatar {
  width: 36px; height: 36px;
  background: var(--xl-color-surface-alt);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-md);
  display: inline-flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  color: var(--xl-color-info);
  overflow: hidden;
}
.xl-cou-row__avatar img { max-width: 100%; max-height: 100%; object-fit: contain }
.xl-cou-row__name {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  font-size: 14px; font-weight: 500; color: var(--xl-color-text);
}
.xl-cou-row__sub {
  font-size: 12px; color: var(--xl-color-text-muted); margin-top: 2px;
}
.xl-cou-row__actions { display: flex; align-items: center; gap: 6px; flex-shrink: 0 }
.xl-cou-row__btn {
  font: 500 12px/1 var(--xl-font-sans);
  background: transparent; border: none; cursor: pointer;
  padding: 6px 10px; border-radius: var(--xl-radius-sm);
}
.xl-cou-row__btn--edit { color: var(--xl-color-info) }
.xl-cou-row__btn--edit:hover { background: rgba(37,99,235,0.08) }
[data-xl-theme="dark"] .xl-cou-row__btn--edit:hover { background: rgba(96,165,250,0.14) }
.xl-cou-row__btn--del { color: var(--xl-color-danger) }
.xl-cou-row__btn--del:hover { background: rgba(220,38,38,0.08) }
.xl-cou-row__btn:focus-visible { outline: 2px solid var(--xl-color-focus); outline-offset: 2px }

.xl-cou-empty {
  padding: 40px 16px;
  text-align: center;
  color: var(--xl-color-text-muted);
  font-size: 13px;
}
.xl-cou-empty__ico { color: var(--xl-color-border-strong); margin-bottom: 8px }
.xl-cou-skeleton { padding: 12px 16px; display: flex; flex-direction: column; gap: 10px }
`;

function Row({ courier: c, onEdit, onDelete }) {
  return (
    <div className="xl-cou-row">
      <div className="xl-cou-row__left">
        <span className="xl-cou-row__avatar" aria-hidden="true">
          {c.logo_url
            ? <img src={c.logo_url} alt="" />
            : <IconTruck />}
        </span>
        <div>
          <div className="xl-cou-row__name">
            <span>{c.nombre}</span>
            {!c.is_active && <XLBadge variant="neutral">Inactivo</XLBadge>}
            {c.url_base_tracking && <XLBadge variant="success" dot>Con rastreo</XLBadge>}
          </div>
          {c.pais && <div className="xl-cou-row__sub">{c.pais}</div>}
        </div>
      </div>
      <div className="xl-cou-row__actions">
        <button type="button" className="xl-cou-row__btn xl-cou-row__btn--edit"
          onClick={() => onEdit(c)}>
          Editar
        </button>
        <button type="button" className="xl-cou-row__btn xl-cou-row__btn--del"
          onClick={() => onDelete(c)}>
          Desactivar
        </button>
      </div>
    </div>
  );
}

export default function CouriersListXL({ items, loading, q, setQ, onEdit, onDelete }) {
  useEffect(() => { ensureKeyframes(); injectCSS(CSS_ID, CSS); }, []);

  return (
    <div className="xl-cou-card">
      <div className="xl-cou-card__hd">
        <h3 className="xl-cou-card__title">
          <span className="xl-cou-card__title-ico"><IconTruck /></span>
          Listado
        </h3>
        <input
          type="search"
          className="xl-cou-search"
          value={q || ''}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por nombre..."
          aria-label="Buscar courier"
        />
      </div>
      <div>
        {loading && (
          <div className="xl-cou-skeleton" aria-busy="true">
            <XLSkeleton variant="line" height={36} />
            <XLSkeleton variant="line" height={36} />
            <XLSkeleton variant="line" height={36} />
          </div>
        )}
        {!loading && (!items || items.length === 0) && (
          <div className="xl-cou-empty" role="status">
            <div className="xl-cou-empty__ico"><IconTruck /></div>
            Sin couriers aun.
          </div>
        )}
        {!loading && items && items.map((c) => (
          <Row key={c.id} courier={c} onEdit={onEdit} onDelete={onDelete} />
        ))}
      </div>
    </div>
  );
}
